import { useEffect, useState } from "react";
import {
  PrivacyCenterIntegrationsDto,
  PrivacyCenterIntegrationVendorDto,
} from "../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { usePrimaryOrganization } from "./useOrganization";
import { usePrivacyCenter } from "./usePrivacyCenter";
import { type AuthenticatedConnectUser, paragon } from "@useparagon/connect";
import QueryString from "qs";

// Paragon seems to choke on non-ASCII characters. Try to roughly map things to ASCII for the org name.
const toAscii = (str) => {
  return (
    str
      .normalize("NFD") // Normalize to decompose characters
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks (accents, etc.)
      // eslint-disable-next-line no-control-regex
      .replace(/[^\x00-\x7F]/g, "")
  ); // Remove non-ASCII characters
};

export const useParagon = (settings?: PrivacyCenterIntegrationsDto) => {
  const [organization, orgRequest] = usePrimaryOrganization();
  const [privacyCenter, privacyCenterRequest] = usePrivacyCenter(organization.id);

  const paragonUserInfo = paragon.getUser();

  const [isAuthorized, setIsAuthorized] = useState(false);

  const [isShopifyConnected, setIsShopifyConnected] = useState(false);
  const [isShopifyLoading, setIsShopifyLoading] = useState(false);

  const paragonUserId = (paragonUserInfo as AuthenticatedConnectUser)?.userId;
  // It's possible for an e.g., admin user to switch orgs. The cookie from `connect.paragon.com` will still have the old
  // org's credentials, so check that the user (org) ID still matches the privacy center before we say we're authorized.
  // Otherwise, re-authenticate with Paragon to get the correct user ID.
  useEffect(() => {
    setIsAuthorized(paragonUserInfo?.authenticated && paragonUserInfo.userId === privacyCenter?.id);
    if ((paragonUserInfo as AuthenticatedConnectUser).integrations?.shopify?.enabled) {
      setIsShopifyConnected(true);
    }
  }, [paragonUserInfo, paragonUserId, privacyCenter?.id]);

  useEffect(() => {
    if (!settings?.featureEnabled) {
      return;
    }

    if (isAuthorized) {
      // Already connected
      return;
    }

    if (!settings?.paragonConnectedUserJWT || !settings?.paragonProjectId) {
      // No JWT or Project ID yet
      return;
    }

    paragon
      .authenticate(settings.paragonProjectId, settings.paragonConnectedUserJWT, {
        metadata: {
          Name: toAscii(organization.name),
        },
      })
      .then((_resolved) => {
        setIsAuthorized(true);
      })
      .catch(() => {
        // Paragon is *really* aggressive about caching the JWT client-side in their cookie, have to catch the exception
        // and get a new auth token.
        setIsAuthorized(false);
      });
  }, [settings, isAuthorized, organization]);

  return {
    requests: [orgRequest, privacyCenterRequest],
    isAuthorized,
    isShopifyConnected,
    isShopifyLoading,
    onConnectOrEditVendor: (vendor: PrivacyCenterIntegrationVendorDto) =>
      new Promise<void>((resolve, reject) => {
        paragon.connect(vendor.vendorAutomationName, {
          onUninstall: () => {
            resolve();
          },
          onSuccess: () => {
            resolve();
          },
          onError: reject,
        });
      }),
    /**
     * @param connectionSuccessCB A callback (e.g. "getLatestData") to run after a successful Shopify connection. Passed
     *                            in instead of relying on the caller's `.then` to design around a dependency loop.
     */
    connectShopify: (connectionSuccessCB: () => void) => {
      const query = QueryString.parse(location.search.replace(/^\?/, ""));
      const { appIntegration, code, shop } = query;
      let shopName = "";
      if (shop) {
        const matches = shop.toString().match(/^([a-zA-Z0-9][a-zA-Z0-9-]*)\.myshopify\.com/);
        shopName = matches[1];
      }
      return new Promise<void>((resolve, reject) => {
        if (
          isShopifyConnected ||
          isShopifyLoading ||
          appIntegration != "shopify" ||
          !shopName ||
          !code
        ) {
          resolve();
          return;
        }
        setIsShopifyLoading(true);
        connectShopifyInner(code.toString(), shopName, (e) => {
          setIsShopifyLoading(false);
          if (e) {
            reject(e);
          } else {
            setIsShopifyConnected(true);
            connectionSuccessCB();
            resolve();
          }
        });
      });
    },
  };
};

// Shopify uses a separate process from the standard Paragon flow.
const MAX_RETRIES = 5;
const ATTEMPT_DELAY = 1000;
const connectShopifyInner = async (
  authorizationCode: string,
  shopName: string,
  cb: (e?: Error) => void,
  retries = 0,
) => {
  const integrationOptions = {};
  integrationOptions["SHOP_NAME"] = shopName;
  try {
    await paragon.completeInstall("shopify", {
      authorizationCode: authorizationCode,
      redirectUrl: `${location.protocol}//${location.host}/webhooks/oauth/shopify/complete`,
      integrationOptions,
    });
    cb();
  } catch (e) {
    if (e.message.includes("is not a valid integration type") && retries < MAX_RETRIES) {
      setTimeout(() => {
        connectShopifyInner(authorizationCode, shopName, cb, retries + 1);
      }, ATTEMPT_DELAY);
      return;
    }
    cb(e);
  }
};
